import React, { useContext } from 'react';

import { PositionableTextDocument } from '@src/lib/PositionableTextDocument';
import type { TRect } from '@src/types/misc';
import { WysiwygEditorStateContext } from '@src/contexts/wysiwyg/editor';

function getLines(lineCount: number, wordsAndPositions) {
  const wordsAndPositionsCopy = [...wordsAndPositions];
  const STARTING_X = -100000;
  let linesFound = 1;
  let lastX = STARTING_X;
  const words = [];

  while (wordsAndPositionsCopy.length > 0 && linesFound <= lineCount) {
    const nextWord = wordsAndPositionsCopy.shift();

    if (nextWord.pos.x < lastX) {
      linesFound += 1;
      lastX = STARTING_X;
    }

    if (linesFound <= lineCount) {
      words.push(nextWord);
      lastX = nextWord.pos.x;
    }
  }

  return words;
}

function RenderedText({
  backgroundColor,
  children,
  editorContent,
  maxLines,
  textContent,
  location,
  viewbox
}: {
  backgroundColor: string | null | undefined;
  children?: React.ReactElement<React.ComponentProps<any>, any>;
  editorContent: PositionableTextDocument;
  maxLines?: number;
  textContent: string;
  location: TRect;
  viewbox: {
    height: number;
    width: number;
  };
}) {
  const { domScaleBasisDimensions } = useContext(WysiwygEditorStateContext);
  const wordsAndPositions = editorContent.toWordsWithAttributeAndPositions();
  const renderableWordsAndPositions =
    wordsAndPositions.length > 0 && maxLines ? getLines(maxLines, wordsAndPositions) : wordsAndPositions;

  return (
    <g transform={`translate(${location.x * viewbox.width} ${location.y * viewbox.height})`}>
      {backgroundColor && backgroundColor !== '#ffffff' && (
        <rect height="100%" width="100%" x="0" y="0" fill={backgroundColor} />
      )}
      <text>
        {textContent.length > 0
          ? renderableWordsAndPositions.map((tb) => {
              const fontSize = tb.attrs.get('font-size');

              if (typeof fontSize === 'number') {
                return (
                  <tspan
                    key={tb.pos.o}
                    x={viewbox.width * tb.pos.x}
                    y={viewbox.height * tb.pos.y}
                    textLength={viewbox.width * tb.pos.w}
                    fill={tb.attrs.get('color')}
                    style={{
                      fontSize: (fontSize / domScaleBasisDimensions.height) * viewbox.height,
                      fontWeight: tb.attrs.get('font-weight'),
                      fontFamily: tb.attrs.get('font-family'),
                      fontStyle: tb.attrs.get('font-style')
                    }}
                  >
                    {tb.text}
                  </tspan>
                );
              } else {
                return null;
              }
            })
          : children}
      </text>
    </g>
  );
}

export default RenderedText;
